import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Plus, Search, Filter, Download, Upload, ArrowLeft } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { useParams, useNavigate } from 'react-router-dom';
import Pagination from '@/components/Pagination';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import InventarioLenceriaTable from './components/InventarioLenceriaTable';
import InventarioLenceriaForm from './components/InventarioLenceriaForm';
import { fetchInventarioLenceria, createInventarioLenceria, updateInventarioLenceria, deleteInventarioLenceria } from './services/inventarioLenceria.services';

const ITEMS_PER_PAGE = 20;
const DEFAULT_FILTERS = { estado: 'Activo' };

const InventarioLenceriaPage = () => {
  const { hospedajeId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Estados principales
  const [inventario, setInventario] = useState([]);
  const [hospedaje, setHospedaje] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  // Estados del formulario
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'

  // Mock data del hospedaje - reemplazar con llamada real al API
  useEffect(() => {
    if (hospedajeId) {
      setHospedaje({
        _id: hospedajeId,
        nombrePropiedad: "Apto 107",
        direccion: "Calle 45 #23-67",
        coordinador: {
          id: "68ee96b27238402c10e314cd",
          nombre: "KEVIN TAUSA"
        },
        edificio: {
          id: "68ed276d40d3e05519e92e99",
          nombre: "Botanica",
          ciudad: "Medellín"
        }
      });
    }
  }, [hospedajeId]);

  const fetchAndSetInventario = useCallback(async () => {
    if (!hospedajeId) return;
    
    setIsLoading(true);
    try {
      const offset = currentPage;
      const query = {
        ...filters,
        'propiedad.id': hospedajeId,
        ...(searchTerm && { nombre: { $regex: searchTerm, $options: 'i' } })
      };

      const { data, totalRecords } = await fetchInventarioLenceria({ 
        limit: ITEMS_PER_PAGE, 
        offset, 
        query 
      });
      
      setInventario(data || []);
      setTotalItems(totalRecords || 0);
    } catch (error) {
      console.error('Error al obtener inventario:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el inventario de lencería",
        variant: "destructive",
      });
      setInventario([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, searchTerm, hospedajeId, toast]);

  useEffect(() => {
    fetchAndSetInventario();
  }, [fetchAndSetInventario]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setIsDrawerOpen(false);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleRemoveFilter = (filterKey) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormMode('create');
    setIsFormOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const handleDelete = async (item) => {
    try {
      await deleteInventarioLenceria(item._id);
      toast({
        title: "Éxito",
        description: `${item.nombre} eliminado correctamente`,
      });
      fetchAndSetInventario();
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el elemento",
        variant: "destructive",
      });
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      const inventarioData = {
        ...formData,
        propiedad: {
          id: hospedaje._id,
          nombre: hospedaje.nombrePropiedad,
          direccion: hospedaje.direccion,
          coordinador: hospedaje.coordinador,
          edificio: hospedaje.edificio
        }
      };

      if (formMode === 'create') {
        await createInventarioLenceria(inventarioData);
        toast({
          title: "Éxito",
          description: "Elemento agregado al inventario correctamente",
        });
      } else {
        await updateInventarioLenceria(editingItem._id, inventarioData);
        toast({
          title: "Éxito",
          description: "Elemento actualizado correctamente",
        });
      }
      
      setIsFormOpen(false);
      setEditingItem(null);
      fetchAndSetInventario();
    } catch (error) {
      console.error('Error al guardar:', error);
      toast({
        title: "Error",
        description: `No se pudo ${formMode === 'create' ? 'crear' : 'actualizar'} el elemento`,
        variant: "destructive",
      });
    }
  };

  const filterFields = [
    {
      key: 'estado',
      label: 'Estado',
      type: 'select',
      options: [
        { value: 'Activo', label: 'Activo' },
        { value: 'Inactivo', label: 'Inactivo' }
      ]
    },
    {
      key: 'nombre',
      label: 'Nombre',
      type: 'text',
      placeholder: 'Buscar por nombre...'
    }
  ];

  if (isLoading && inventario.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Cargando inventario de lencería...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <Card className="card-gradient-bg shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/hospedaje/${hospedajeId}`)}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Volver al Dashboard</span>
              </Button>
            </div>
            <Button onClick={handleCreate} className="flex items-center space-x-2">
              <Plus className="h-4 w-4" />
              <span>Agregar Elemento</span>
            </Button>
          </div>
          
          <div className="flex items-center space-x-4 pt-4 border-t">
            <Package className="h-8 w-8 text-foreground" />
            <div>
              <CardTitle className="text-2xl font-bold text-foreground">
                Inventario de Lencería
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {hospedaje && (
                  <div className="flex items-center space-x-4 mt-2">
                    <span>{hospedaje.nombrePropiedad}</span>
                    <span>•</span>
                    <span>{hospedaje.edificio.nombre}</span>
                    <span>•</span>
                    <span>{hospedaje.coordinador.nombre}</span>
                  </div>
                )}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Controles */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-1 gap-4 items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Buscar elementos..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
       {/*        <Button
                variant="outline"
                onClick={() => setIsDrawerOpen(true)}
                className="flex items-center space-x-2"
              >
                <Filter className="h-4 w-4" />
                <span>Filtros</span>
              </Button> */}
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Upload className="h-4 w-4 mr-2" />
                Importar
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            </div>
          </div>

{/*           <AppliedFilters
            filters={filters}
            fields={filterFields}
            searchTerm={searchTerm}
            onRemoveFilter={handleRemoveFilter}
            onClearFilters={handleClearFilters}
            className="mt-4"
          /> */}
        </CardContent>
      </Card>

      {/* Tabla */}
      <InventarioLenceriaTable
        inventario={inventario}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Paginación */}
      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={handlePageChange}
        />
      )}

      {/* Drawer de filtros */}
      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onApplyFilters={handleFilterChange}
        fields={filterFields}
        currentFilters={filters}
      />

      {/* Formulario */}
      <InventarioLenceriaForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        editingItem={editingItem}
        mode={formMode}
        hospedaje={hospedaje}
      />
    </motion.div>
  );
};

export default InventarioLenceriaPage;
